import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import Breadcrumb from '../Breadcrumb';
import '../ProjectManagement.css';
import MobileHeader from '../MobileHeader';
import useIsMobile from '../../hooks/useIsMobile';
import CodeEditor from '../CodeEditor';

interface BuildProps {
  projectId: string;
}

const Build: React.FC<BuildProps> = ({ projectId }) => {
  const [inputFields, setInputFields] = useState([{
    name: 'message',
    type: 'string',
    description: 'User message for sentiment prediction',
    required: true,
    defaultValue: ''
  }]);

  const [outputFields, setOutputFields] = useState([
    { name: 'input_msg', value: '$.input.message' },
    { name: 'sentiment', value: '$.step1.output.sentiment_pred' },
    { name: 'timestamp', value: '$.metadata.timestamp' }
  ]);

  const [steps, setSteps] = useState<Array<{id: number, name: string, type: 'llm' | 'processing', modelName?: string, systemPrompt?: string, userPrompt?: string, temperature?: number, outputs?: Array<{name: string, description: string}>, processingType?: string, processingConfig?: any}>>([]);
  const [nextStepId, setNextStepId] = useState(1);
  const [versions, setVersions] = useState<Array<{version: string, name: string, display: string}>>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('build');
  const [showChat, setShowChat] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<number | null>(null);
  const [chatMessages, setChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [userInput, setUserInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPipelineChat, setShowPipelineChat] = useState(false);
  const [pipelineChatMessages, setPipelineChatMessages] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [pipelineUserInput, setPipelineUserInput] = useState('');
  const [isPipelineGenerating, setIsPipelineGenerating] = useState(false);
  const [draggedStepId, setDraggedStepId] = useState<number | null>(null);
  const [autocomplete, setAutocomplete] = useState<{show: boolean, suggestions: string[], position: {top: number, left: number}, selectedIndex: number, inputRef: HTMLInputElement | HTMLTextAreaElement | null}>({show: false, suggestions: [], position: {top: 0, left: 0}, selectedIndex: 0, inputRef: null});
  const [newStepIds, setNewStepIds] = useState<Set<number>>(new Set());
  const [viewportPosition, setViewportPosition] = useState(0);
  const [currentVisibleStep, setCurrentVisibleStep] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const convertInputFieldsToSchema = () => {
    const properties: any = {};
    const required: string[] = [];
    
    for (const field of inputFields) {
      properties[field.name] = {
        type: field.type,
        description: field.description
      };
      
      if (field.required) {
        required.push(field.name);
      } else if (field.defaultValue) {
        properties[field.name].default = field.defaultValue;
      }
    }
    
    return {
      type: 'object',
      properties,
      required
    };
  };

  const convertOutputFieldsToMapping = () => {
    const mapping: any = {};
    for (const field of outputFields) {
      mapping[field.name] = field.value;
    }
    return mapping;
  };

  const compileJobConfig = () => {
    return {
      inputSchema: convertInputFieldsToSchema(),
      outputMapping: convertOutputFieldsToMapping(),
      steps: steps.map(step => {
        if (step.type === 'processing') {
          return {
            id: step.id,
            name: step.name,
            step_type: 'processing',
            processingType: step.processingType,
            processingConfig: step.processingConfig,
            outputs: step.outputs
          };
        } else {
          return {
            id: step.id,
            name: step.name,
            step_type: 'llm',
            modelName: step.modelName,
            systemPrompt: step.systemPrompt,
            userPrompt: step.userPrompt,
            temperature: step.temperature,
            outputs: step.outputs
          };
        }
      })
    };
  };

  const generateSuggestions = () => {
    const suggestions: string[] = [];
    
    // Add input field suggestions
    inputFields.forEach(field => {
      if (field.name) {
        suggestions.push(`$.input.${field.name}`);
      }
    });
    
    // Add step output suggestions
    steps.forEach(step => {
      if (step.name && step.outputs) {
        step.outputs.forEach(output => {
          if (output.name) {
            suggestions.push(`$.${step.name}.output.${output.name}`);
          }
        });
      }
    });
    
    // Add metadata suggestions
    suggestions.push('$.metadata.timestamp');
    suggestions.push('$.metadata.requestId');
    
    return suggestions;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, onChange: (value: string) => void) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    onChange(value);
    
    console.log('Input change:', { value, cursorPos, lastTwo: value.substring(cursorPos - 2, cursorPos) });
    
    // Check if user typed "$." at cursor position
    if (value.substring(cursorPos - 2, cursorPos) === '$.') {
      const rect = e.target.getBoundingClientRect();
      const suggestions = generateSuggestions();
      
      console.log('Showing autocomplete:', suggestions);
      
      setAutocomplete({
        show: true,
        suggestions,
        position: { top: rect.bottom + 5, left: rect.left },
        selectedIndex: 0,
        inputRef: e.target
      });
    } else if (autocomplete.show && autocomplete.inputRef === e.target) {
      // Filter suggestions based on current input
      const beforeCursor = value.substring(0, cursorPos);
      const dollarIndex = beforeCursor.lastIndexOf('$.');
      
      if (dollarIndex !== -1) {
        const searchTerm = beforeCursor.substring(dollarIndex);
        const filtered = generateSuggestions().filter(s => s.startsWith(searchTerm));
        
        console.log('Filtering suggestions:', { searchTerm, filtered });
        
        if (filtered.length > 0) {
          setAutocomplete(prev => ({ ...prev, suggestions: filtered, selectedIndex: 0 }));
        } else {
          setAutocomplete(prev => ({ ...prev, show: false }));
        }
      } else {
        setAutocomplete(prev => ({ ...prev, show: false }));
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!autocomplete.show) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAutocomplete(prev => ({
        ...prev,
        selectedIndex: Math.min(prev.selectedIndex + 1, prev.suggestions.length - 1)
      }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAutocomplete(prev => ({
        ...prev,
        selectedIndex: Math.max(prev.selectedIndex - 1, 0)
      }));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectSuggestion(autocomplete.suggestions[autocomplete.selectedIndex]);
    } else if (e.key === 'Escape') {
      setAutocomplete(prev => ({ ...prev, show: false }));
    }
  };

  const getOperationSymbol = (operation: string) => {
    switch (operation) {
      case '*': return '×';
      case '/': return '÷';
      default: return operation;
    }
  };

  const renderProcessingConfig = (step: any) => {
    const config = step.processingConfig || { operations: [] };
    
    if (step.processingType === 'number_operations') {
      return (
        <div className="config-field">
          <label>Number Operations</label>
          <div className="processing-operations">
            <input
              type="text"
              value={config.baseVariable || ''}
              data-field-type="processingBaseVariable"
              data-step-id={step.id}
              onChange={(e) => handleInputChange(e, (value) => updateProcessingBaseVariable(step.id, value))}
              onKeyDown={handleKeyDown}
              placeholder="Variable name"
              style={{ marginBottom: '1rem' }}
            />
            {config.operations.map((op: any, index: number) => (
              <div key={index} className="processing-operation-row">
                <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 'bold' }}>{getOperationSymbol(op.operation)}</span>
                <input
                  type="text"
                  value={op.value || ''}
                  data-field-type="processingOperationValue"
                  data-step-id={step.id}
                  data-operation-index={index}
                  onChange={(e) => handleInputChange(e, (value) => updateProcessingOperation(step.id, index, 'value', value))}
                  onKeyDown={handleKeyDown}
                  placeholder="Value"
                  style={{ flex: 1 }}
                />
                <button
                  onClick={() => removeProcessingOperation(step.id, index)}
                  className="processing-remove-btn"
                >
                  ×
                </button>
              </div>
            ))}
            <div className="processing-operation-buttons">
              <button onClick={() => addProcessingOperation(step.id, '+')} className="processing-operation-btn">+</button>
              <button onClick={() => addProcessingOperation(step.id, '-')} className="processing-operation-btn">-</button>
              <button onClick={() => addProcessingOperation(step.id, '*')} className="processing-operation-btn">×</button>
              <button onClick={() => addProcessingOperation(step.id, '/')} className="processing-operation-btn">÷</button>
            </div>
          </div>
        </div>
      );
    } else if (step.processingType === 'string_operations') {
      return (
        <div className="config-field">
          <label>String Operations</label>
          <div className="processing-string-config">
            <input
              type="text"
              value={config.input || ''}
              data-field-type="processingStringInput"
              data-step-id={step.id}
              onChange={(e) => handleInputChange(e, (value) => updateProcessingConfig(step.id, { ...config, input: value }))}
              onKeyDown={handleKeyDown}
              placeholder="Text or variable name"
            />
            <select
              value={config.operation || 'lowercase'}
              onChange={(e) => updateProcessingConfig(step.id, { ...config, operation: e.target.value })}
            >
              <option value="lowercase">Convert to Lower Case</option>
              <option value="uppercase">Convert to Upper Case</option>
              <option value="capitalize">Capitalize</option>
            </select>
          </div>
        </div>
      );
    } else if (step.processingType === 'python') {
      return (
        <div className="config-field">
          <label>Python Code</label>
          <div style={{ 
            fontSize: '0.85rem', 
            color: '#6b7280', 
            fontStyle: 'italic', 
            marginBottom: '0.5rem',
            lineHeight: '1.4',
            textAlign: 'left'
          }}>
            This Python code will be executed in a locked down environment with no internet access and only basic native Python libraries are available.
          </div>
          <CodeEditor
            value={config.code || 'def run(context):\n    # Access context data: context["input"], context["step1"]["output"], etc.\n    # Return a dictionary with your results\n    return {}'}
            onChange={(value) => updateProcessingConfig(step.id, { ...config, code: value })}
            placeholder="Enter Python code"
            rows={10}
          />
        </div>
      );
    } else if (step.processingType === 'list_operations') {
      return (
        <div className="config-field">
          <label>List Operations</label>
          <div className="processing-list-config">
            <input
              type="text"
              value={config.listVariable || ''}
              data-field-type="processingListVariable"
              data-step-id={step.id}
              onChange={(e) => handleInputChange(e, (value) => updateProcessingConfig(step.id, { ...config, listVariable: value }))}
              onKeyDown={handleKeyDown}
              placeholder="List variable or JSONPath (e.g., $.input.items)"
            />
            <select
              value={config.operation || 'sum'}
              onChange={(e) => updateProcessingConfig(step.id, { ...config, operation: e.target.value })}
            >
              <option value="sum">Sum</option>
              <option value="deduplicate">Deduplicate</option>
              <option value="lowercase">Convert to Lower Case</option>
              <option value="uppercase">Convert to Upper Case</option>
              <option value="capitalize">Capitalize</option>
            </select>
          </div>
        </div>
      );
    }
    return null;
  };

  const updateProcessingConfig = (stepId: number, config: any) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, processingConfig: config } : step
    ));
  };

  const updateProcessingOperation = (stepId: number, opIndex: number, field: string, value: string) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const config = step.processingConfig || { baseVariable: '', operations: [] };
        const operations = [...config.operations];
        operations[opIndex] = { ...operations[opIndex], [field]: value };
        return { ...step, processingConfig: { ...config, operations } };
      }
      return step;
    }));
  };

  const updateProcessingBaseVariable = (stepId: number, value: string) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const config = step.processingConfig || { baseVariable: '', operations: [] };
        return { ...step, processingConfig: { ...config, baseVariable: value } };
      }
      return step;
    }));
  };

  const addProcessingOperation = (stepId: number, operation: string) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const config = step.processingConfig || { baseVariable: '', operations: [] };
        const operations = [...config.operations, { operation, value: '' }];
        return { ...step, processingConfig: { ...config, operations } };
      }
      return step;
    }));
  };

  const removeProcessingOperation = (stepId: number, opIndex: number) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const config = step.processingConfig || { baseVariable: '', operations: [] };
        const operations = config.operations.filter((_: any, index: number) => index !== opIndex);
        return { ...step, processingConfig: { ...config, operations } };
      }
      return step;
    }));
  };

  const selectSuggestion = (suggestion: string) => {
    if (!autocomplete.inputRef) return;
    
    const input = autocomplete.inputRef;
    const value = input.value;
    const cursorPos = input.selectionStart || 0;
    const beforeCursor = value.substring(0, cursorPos);
    const dollarIndex = beforeCursor.lastIndexOf('$.');
    
    if (dollarIndex !== -1) {
      const newValue = value.substring(0, dollarIndex) + suggestion + value.substring(cursorPos);
      
      // Create a synthetic event to trigger the onChange handler
      const syntheticEvent = {
        target: { ...input, value: newValue },
        currentTarget: input
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      
      // Find and call the appropriate onChange handler
      if (input.dataset.fieldType === 'outputValue') {
        const index = parseInt(input.dataset.fieldIndex || '0');
        const newFields = [...outputFields];
        newFields[index].value = newValue;
        setOutputFields(newFields);
      } else if (input.dataset.fieldType === 'defaultValue') {
        const index = parseInt(input.dataset.fieldIndex || '0');
        const newFields = [...inputFields];
        newFields[index].defaultValue = newValue;
        setInputFields(newFields);
      } else if (input.dataset.fieldType === 'systemPrompt') {
        const stepId = parseInt(input.dataset.stepId || '0');
        handleStepConfigChange(stepId, 'systemPrompt', newValue);
      } else if (input.dataset.fieldType === 'userPrompt') {
        const stepId = parseInt(input.dataset.stepId || '0');
        handleStepConfigChange(stepId, 'userPrompt', newValue);
      } else if (input.dataset.fieldType === 'processingBaseVariable') {
        const stepId = parseInt(input.dataset.stepId || '0');
        updateProcessingBaseVariable(stepId, newValue);
      } else if (input.dataset.fieldType === 'processingOperationValue') {
        const stepId = parseInt(input.dataset.stepId || '0');
        const opIndex = parseInt(input.dataset.operationIndex || '0');
        updateProcessingOperation(stepId, opIndex, 'value', newValue);
      } else if (input.dataset.fieldType === 'processingStringInput') {
        const stepId = parseInt(input.dataset.stepId || '0');
        const step = steps.find(s => s.id === stepId);
        if (step) {
          const config = step.processingConfig || {};
          updateProcessingConfig(stepId, { ...config, input: newValue });
        }
      } else if (input.dataset.fieldType === 'processingListVariable') {
        const stepId = parseInt(input.dataset.stepId || '0');
        const step = steps.find(s => s.id === stepId);
        if (step) {
          const config = step.processingConfig || {};
          updateProcessingConfig(stepId, { ...config, listVariable: newValue });
        }
      }
      
      // Update the input value
      input.value = newValue;
    }
    
    setAutocomplete(prev => ({ ...prev, show: false }));
    input.focus();
  };

  const handleAddStep = (type: 'llm' | 'processing' = 'llm', insertIndex?: number) => {
    const newStep = type === 'llm' ? { 
      id: nextStepId, 
      name: `step${nextStepId}`,
      type: 'llm' as const,
      modelName: '',
      systemPrompt: '',
      userPrompt: '',
      temperature: 0,
      outputs: [{ name: '', description: '' }]
    } : {
      id: nextStepId,
      name: `step${nextStepId}`,
      type: 'processing' as const,
      processingType: 'number_operations',
      processingConfig: { baseVariable: '', operations: [] },
      outputs: [{ name: '', description: '' }]
    };
    
    if (insertIndex !== undefined) {
      const newSteps = [...steps];
      newSteps.splice(insertIndex, 0, newStep);
      setSteps(newSteps);
    } else {
      setSteps([...steps, newStep]);
    }
    setNextStepId(nextStepId + 1);
    
    // Add to new steps set for flashing animation
    setNewStepIds(prev => new Set(prev).add(newStep.id));
    
    // Remove from new steps set after animation
    setTimeout(() => {
      setNewStepIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(newStep.id);
        return newSet;
      });
    }, 3000);
  };

  const handleDeleteStep = (stepId: number, stepName: string) => {
    if (window.confirm(`Are you sure you want to delete ${stepName}?`)) {
      setSteps(steps.filter(step => step.id !== stepId));
    }
  };

  const handleDragStart = (e: React.DragEvent, stepId: number) => {
    setDraggedStepId(stepId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStepId: number) => {
    e.preventDefault();
    if (!draggedStepId || draggedStepId === targetStepId) {
      setDraggedStepId(null);
      return;
    }

    const draggedIndex = steps.findIndex(step => step.id === draggedStepId);
    const targetIndex = steps.findIndex(step => step.id === targetStepId);
    
    const newSteps = [...steps];
    const [draggedStep] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(targetIndex, 0, draggedStep);
    
    setSteps(newSteps);
    setDraggedStepId(null);
  };

  const handleDragEnd = () => {
    setDraggedStepId(null);
  };

  const handleStepNameChange = (stepId: number, newName: string) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, name: newName } : step
    ));
  };

  const handleStepConfigChange = (stepId: number, field: string, value: string | number) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, [field]: value } : step
    ));
  };

  const handleOutputChange = (stepId: number, outputIndex: number, field: 'name' | 'description', value: string) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const currentOutputs = step.outputs || [{ name: '', description: '' }];
        const newOutputs = [...currentOutputs];
        newOutputs[outputIndex] = { ...newOutputs[outputIndex], [field]: value };
        return { ...step, outputs: newOutputs };
      }
      return step;
    }));
  };

  const addOutput = (stepId: number) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const currentOutputs = step.outputs || [{ name: '', description: '' }];
        return { ...step, outputs: [...currentOutputs, { name: '', description: '' }] };
      }
      return step;
    }));
  };

  const removeOutput = (stepId: number, outputIndex: number) => {
    setSteps(steps.map(step => {
      if (step.id === stepId) {
        const currentOutputs = step.outputs || [{ name: '', description: '' }];
        const newOutputs = currentOutputs.filter((_, index) => index !== outputIndex);
        return { ...step, outputs: newOutputs };
      }
      return step;
    }));
  };

  const showToast = (message: string, type: 'error' | 'success' = 'error') => {
    const existingToasts = document.querySelectorAll('.toast-notification');
    existingToasts.forEach(toast => document.body.removeChild(toast));
    
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem 1rem 1rem;
      background: ${type === 'error' ? '#fed7d7' : '#c6f6d5'};
      color: ${type === 'error' ? '#c53030' : '#2f855a'};
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      max-width: 300px;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    `;
    
    const messageSpan = document.createElement('span');
    messageSpan.textContent = message;
    messageSpan.style.flex = '1';
    
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      color: inherit;
      font-size: 1.2rem;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    `;
    closeBtn.onclick = () => document.body.removeChild(toast);
    
    toast.appendChild(messageSpan);
    toast.appendChild(closeBtn);
    document.body.appendChild(toast);
  };
  
  const showToastList = (messages: string[], type: 'error' | 'success' = 'error') => {
    const existingToasts = document.querySelectorAll('.toast-notification');
    existingToasts.forEach(toast => document.body.removeChild(toast));
    
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'error' ? '#fed7d7' : '#c6f6d5'};
      color: ${type === 'error' ? '#c53030' : '#2f855a'};
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      max-width: 400px;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    `;
    
    const messageDiv = document.createElement('div');
    messageDiv.style.flex = '1';
    
    const title = document.createElement('div');
    title.textContent = `${messages.length} Validation Error${messages.length > 1 ? 's' : ''}`;
    title.style.cssText = 'font-weight: bold; margin-bottom: 0.5rem;';
    messageDiv.appendChild(title);
    
    const list = document.createElement('ul');
    list.style.cssText = 'margin: 0; padding-left: 1rem; font-size: 0.9rem;';
    
    messages.forEach(message => {
      const listItem = document.createElement('li');
      listItem.textContent = message;
      listItem.style.marginBottom = '0.25rem';
      list.appendChild(listItem);
    });
    
    messageDiv.appendChild(list);
    
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = `
      background: none;
      border: none;
      color: inherit;
      font-size: 1.2rem;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    `;
    closeBtn.onclick = () => document.body.removeChild(toast);
    
    toast.appendChild(messageDiv);
    toast.appendChild(closeBtn);
    document.body.appendChild(toast);
  };

  const generateSampleJson = () => {
    const sampleData: any = {};
    
    for (const field of inputFields) {
      if (field.type === 'string') {
        sampleData[field.name] = field.description || `sample_${field.name}`;
      } else if (field.type === 'number') {
        sampleData[field.name] = 42;
      } else {
        sampleData[field.name] = `sample_${field.name}`;
      }
    }
    
    return JSON.stringify(sampleData, null, 2);
  };

  const showPopup = () => {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const popup = document.createElement('div');
    popup.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 2rem;
      max-width: 500px;
      width: 90%;
      max-height: 80%;
      overflow-y: auto;
    `;
    
    const title = document.createElement('h3');
    title.textContent = 'Test Input JSON';
    title.style.marginTop = '0';
    
    const textarea = document.createElement('textarea');
    textarea.value = generateSampleJson();
    textarea.style.cssText = `
      width: 100%;
      height: 300px;
      padding: 1rem;
      border: 2px solid #e2e8f0;
      border-radius: 6px;
      font-family: Monaco, monospace;
      font-size: 0.9rem;
      resize: vertical;
      box-sizing: border-box;
    `;
    
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
      margin-top: 1rem;
    `;
    
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = `
      background: #f7fafc;
      color: #4a5568;
      border: 2px solid #e2e8f0;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
    `;
    cancelBtn.onclick = () => document.body.removeChild(overlay);
    
    const runBtn = document.createElement('button');
    runBtn.textContent = 'Run';
    runBtn.style.cssText = `
      background: #48bb78;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
    `;
    runBtn.onclick = async () => {
      try {
        runBtn.disabled = true;
        runBtn.textContent = 'Running...';
        
        // const jobConfig = {
        //   inputSchema: convertInputFieldsToSchema(),
        //   outputMapping: convertOutputFieldsToMapping(),
        //   steps: steps
        // };

        const jobConfig = compileJobConfig();
        
        const testEvent = JSON.parse(textarea.value);
        
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/run`, {
          method: 'POST',
          headers: {
            'Authorization': token || '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            jobConfig,
            testEvent,
            project_id: projectId
          })
        });
        
        const result = await response.json();
        showResult(response.status, result);
        
      } catch (error) {
        showResult(500, { error: error instanceof Error ? error.message : 'Unknown error' });
      }
    };
    
    const showResult = (statusCode: number, payload: any) => {
      const resultSection = document.createElement('div');
      resultSection.style.cssText = `
        margin-bottom: 1.5rem;
        border-bottom: 1px solid #e2e8f0;
        padding: 1rem;
        border-radius: 8px;
        background: ${statusCode === 200 ? '#c6f6d5' : '#fed7d7'};
      `;
      
      const statusDiv = document.createElement('div');
      statusDiv.style.cssText = `
        font-weight: bold;
        margin-bottom: 1rem;
        color: ${statusCode === 200 ? '#2f855a' : '#c53030'};
      `;
      statusDiv.textContent = `Status: ${statusCode}`;
      
      const responseArea = document.createElement('textarea');
      responseArea.value = JSON.stringify(payload, null, 2);
      responseArea.style.cssText = `
        width: 100%;
        height: 200px;
        padding: 1rem;
        border: none;
        border-radius: 6px;
        font-family: Monaco, monospace;
        font-size: 0.9rem;
        resize: vertical;
        box-sizing: border-box;
        background: white;
      `;
      responseArea.readOnly = true;
      
      resultSection.appendChild(statusDiv);
      resultSection.appendChild(responseArea);
      
      popup.insertBefore(resultSection, textarea);
      
      runBtn.disabled = false;
      runBtn.textContent = 'Run Again';
    };
    
    buttonContainer.appendChild(cancelBtn);
    buttonContainer.appendChild(runBtn);
    
    popup.appendChild(title);
    popup.appendChild(textarea);
    popup.appendChild(buttonContainer);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
  };

  const handleRun = async () => {
    try {
      const config = compileJobConfig();
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config })
      });
      
      const result = await response.json();
      
      if (!result.is_valid) {
        const errorMessages = result.errors.map((error: any) => `${error.stage}: ${error.error}`);
        showToastList(errorMessages, 'error');
        return;
      }
      
      showPopup();
      
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Validation failed', 'error');
    }
  };

  const loadVersions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions`, {
        method: 'GET',
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allVersions = [
          { version: 'build', name: 'build', display: 'build (current)' },
          ...data.versions
        ];
        setVersions(allVersions);
      } else {
        setVersions([{ version: 'build', name: 'build', display: 'build (current)' }]);
      }
    } catch (error) {
      setVersions([{ version: 'build', name: 'build', display: 'build (current)' }]);
    }
  };

  const loadConfig = async (version: string = 'build') => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      let url;
      if (version === 'build') {
        url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/build/config`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/versions/${version}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const config = await response.json();
        const configData = version === 'build' ? config : config.config;
        
        if (configData.inputSchema && configData.inputSchema.properties) {
          const fields = Object.entries(configData.inputSchema.properties).map(([name, prop]: [string, any]) => ({
            name,
            type: prop.type || 'string',
            description: prop.description || '',
            required: configData.inputSchema.required?.includes(name) || false,
            defaultValue: prop.default || ''
          }));
          setInputFields(fields.length > 0 ? fields : [{ name: '', type: 'string', description: '', required: true, defaultValue: '' }]);
        }
        
        if (configData.outputMapping) {
          const fields = Object.entries(configData.outputMapping).map(([name, value]: [string, any]) => ({
            name,
            value: value || ''
          }));
          setOutputFields(fields.length > 0 ? fields : [{ name: '', value: '' }]);
        }
        
        const loadedSteps = (configData.steps || []).map((step: any) => {
          if (!step.step_type || step.step_type === 'llm') {
            return { ...step, type: 'llm' };
          } else {
            return {
              ...step,
              type: 'processing',
              processingType: step.processingType || 'number_operations',
              processingConfig: step.processingConfig || { baseVariable: '', operations: [] },
              outputs: step.outputs || [{ name: '', description: '' }]
            };
          }
        });
        setSteps(loadedSteps);
        if (loadedSteps.length > 0) {
          setNextStepId(Math.max(...loadedSteps.map((s: any) => s.id)) + 1);
        }
      }
    } catch (error) {
      // Silently fail if config doesn't exist
    }
  };

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version);
    loadConfig(version);
  };

  useEffect(() => {
    loadVersions();
    loadConfig();
  }, [projectId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocomplete.show && !((event.target as Element)?.closest('.autocomplete-dropdown'))) {
        setAutocomplete(prev => ({ ...prev, show: false }));
      }
    };
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollTop / (documentHeight - windowHeight);
      setViewportPosition(scrollPercentage);
      
      // Find step in middle of viewport
      const viewportCenter = scrollTop + windowHeight / 2;
      const allSections = document.querySelectorAll('.pipeline-section');
      
      let visibleStep: string | null = null;
      allSections.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollTop;
        const elementBottom = elementTop + rect.height;
        
        if (elementTop <= viewportCenter && elementBottom >= viewportCenter) {
          const stepNameInput = element.querySelector('.step-name-input') as HTMLInputElement;
          const inputsHeader = element.querySelector('h3')?.textContent;
          
          if (inputsHeader?.includes('Inputs')) {
            visibleStep = 'inputs';
          } else if (inputsHeader?.includes('Outputs')) {
            visibleStep = 'outputs';
          } else if (stepNameInput) {
            visibleStep = stepNameInput.value || `step-${index}`;
          }
        }
      });
      
      setCurrentVisibleStep(visibleStep);
      if (visibleStep) console.log('Currently viewing:', visibleStep);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    // Use interval to check scroll position since scroll events aren't working
    const scrollInterval = setInterval(handleScroll, 100);
    handleScroll(); // Initial call
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(scrollInterval);
    };
  }, [autocomplete.show]);

  const handleSave = async () => {
    try {
      const config = compileJobConfig();
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/build/config`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        showToast('Configuration saved successfully!', 'success');
      } else {
        showToast('Failed to save configuration', 'error');
      }
      
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Save failed', 'error');
    }
  };

  const handlePublish = async () => {
    try {
      const config = compileJobConfig();
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const validateResponse = await fetch(`${process.env.REACT_APP_API_URL}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config })
      });
      
      const validateResult = await validateResponse.json();
      
      if (!validateResult.is_valid) {
        const errorMessages = validateResult.errors.map((error: any) => `${error.stage}: ${error.error}`);
        showToastList(errorMessages, 'error');
        return;
      }
      
      const versionResponse = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/version`, {
        method: 'GET',
        headers: {
          'Authorization': token || ''
        }
      });
      
      const versionData = await versionResponse.json();
      showPublishPopup(versionData.nextVersion, config);
      
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Publish failed', 'error');
    }
  };

  const showPublishPopup = (nextVersion: number, config: any) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    
    const popup = document.createElement('div');
    popup.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 2rem;
      max-width: 400px;
      width: 90%;
    `;
    
    const title = document.createElement('h3');
    title.textContent = 'Publish Configuration';
    title.style.marginTop = '0';
    
    const versionInfo = document.createElement('p');
    versionInfo.textContent = `Version: ${nextVersion}`;
    versionInfo.style.cssText = 'font-weight: bold; margin: 1rem 0;';
    
    const nameLabel = document.createElement('label');
    nameLabel.textContent = 'Version Name:';
    nameLabel.style.cssText = 'display: block; margin-bottom: 0.5rem; font-weight: bold;';
    
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Enter version name';
    nameInput.style.cssText = `
      width: 100%;
      padding: 0.5rem;
      border: 2px solid #e2e8f0;
      border-radius: 6px;
      margin-bottom: 1rem;
      box-sizing: border-box;
    `;
    
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 1rem;
      justify-content: flex-end;
    `;
    
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.cssText = `
      background: #f7fafc;
      color: #4a5568;
      border: 2px solid #e2e8f0;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
    `;
    cancelBtn.onclick = () => document.body.removeChild(overlay);
    
    const publishBtn = document.createElement('button');
    publishBtn.textContent = 'Confirm Publish';
    publishBtn.style.cssText = `
      background: #4299e1;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
    `;
    publishBtn.onclick = async () => {
      if (!nameInput.value.trim()) {
        nameInput.style.border = '2px solid #e53e3e';
        return;
      }
      
      try {
        publishBtn.disabled = true;
        publishBtn.textContent = 'Publishing...';
        
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/publish`, {
          method: 'POST',
          headers: {
            'Authorization': token || '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectId,
            config,
            versionName: nameInput.value.trim()
          })
        });
        
        if (response.ok) {
          document.body.removeChild(overlay);
          showToast('Configuration published successfully!', 'success');
        } else {
          const error = await response.json();
          showToast(error.error || 'Publish failed', 'error');
        }
        
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Publish failed', 'error');
      } finally {
        publishBtn.disabled = false;
        publishBtn.textContent = 'Confirm Publish';
      }
    };
    
    buttonContainer.appendChild(cancelBtn);
    buttonContainer.appendChild(publishBtn);
    
    popup.appendChild(title);
    popup.appendChild(versionInfo);
    popup.appendChild(nameLabel);
    popup.appendChild(nameInput);
    popup.appendChild(buttonContainer);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    
    nameInput.focus();
  };

  const renderAddStepButton = (insertIndex?: number) => (
    <div className="pipeline-arrow">
      <div className="arrow-down">↓</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button className="add-step-btn" onClick={() => handleAddStep('llm', insertIndex)}>
          Add LLM Step
        </button>
        <button className="add-processing-step-btn" onClick={() => handleAddStep('processing', insertIndex)}>
          Add Processing Step
        </button>
      </div>
      <div className="arrow-down">↓</div>
    </div>
  );

  const renderStep = (step: {id: number, name: string, type: 'llm' | 'processing', modelName?: string, systemPrompt?: string, userPrompt?: string, temperature?: number, outputs?: Array<{name: string, description: string}>, processingType?: string, processingConfig?: any}) => (
    <div 
      key={step.id} 
      className={`pipeline-section ${newStepIds.has(step.id) ? 'flash-border' : ''}`}
      draggable
      onDragStart={(e) => handleDragStart(e, step.id)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, step.id)}
      onDragEnd={handleDragEnd}
      style={{
        opacity: draggedStepId === step.id ? 0.5 : 1,
        cursor: 'move'
      }}
    >
      <div className="section-card">
        <div className="step-header">
          <div className="step-title">
            <span className="step-icon" style={{ cursor: 'move' }}>⋮⋮</span>
            <span className="step-icon">{step.type === 'llm' ? '🔧' : '⚙️'}</span>
            <input
              type="text"
              className="step-name-input"
              value={step.name}
              onChange={(e) => handleStepNameChange(step.id, e.target.value)}
              placeholder="Enter step name"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span 
                style={{ 
                  fontSize: '1.2rem', 
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => {
                  setCurrentStepId(step.id);
                  setShowChat(true);
                  setChatMessages([{role: 'assistant', content: 'Describe to me what you want to build for this step.'}]);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                  if (tooltip) tooltip.style.visibility = 'visible';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                  if (tooltip) tooltip.style.visibility = 'hidden';
                }}
              >
                🤖
              </span>
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#333',
                color: 'white',
                padding: '0.5rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                visibility: 'hidden',
                zIndex: 1000,
                marginBottom: '5px'
              }}>
                Get help from AI assistant
              </div>
            </div>
            <button 
              className="delete-step-btn" 
              onClick={() => handleDeleteStep(step.id, step.name)}
              title="Delete step"
            >
              ×
            </button>
          </div>
        </div>
        <div className="step-config">
          {step.type === 'llm' ? (
            <>
              <div className="config-field">
                <label>Model Name</label>
                <input
                  type="text"
                  value={step.modelName || ''}
                  onChange={(e) => handleStepConfigChange(step.id, 'modelName', e.target.value)}
                  placeholder="e.g., gpt-5, gpt-4, gpt-4o-mini"
                />
              </div>
              <div className="config-field">
                <label>System Prompt</label>
                <textarea
                  value={step.systemPrompt || ''}
                  data-field-type="systemPrompt"
                  data-step-id={step.id}
                  onChange={(e) => handleInputChange(e, (value) => handleStepConfigChange(step.id, 'systemPrompt', value))}
                  onKeyDown={handleKeyDown}
                  placeholder="System instructions for the model"
                  rows={3}
                />
              </div>
              <div className="config-field">
                <label>User Prompt</label>
                <textarea
                  value={step.userPrompt || ''}
                  data-field-type="userPrompt"
                  data-step-id={step.id}
                  onChange={(e) => handleInputChange(e, (value) => handleStepConfigChange(step.id, 'userPrompt', value))}
                  onKeyDown={handleKeyDown}
                  placeholder="User prompt template"
                  rows={3}
                />
              </div>
              <div className="config-field temperature-field">
                <label>Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={step.temperature || 0}
                  onChange={(e) => handleStepConfigChange(step.id, 'temperature', parseFloat(e.target.value) || 0)}
                  placeholder="0.0"
                  className="temperature-input"
                />
              </div>
              <div className="config-field">
                <label>Outputs</label>
                <div className="outputs-section">
                  {(step.outputs || [{ name: '', description: '' }]).map((output, index) => (
                    <div key={index} className="output-row">
                      <input
                        type="text"
                        className="output-name"
                        value={output.name}
                        onChange={(e) => handleOutputChange(step.id, index, 'name', e.target.value)}
                        placeholder="Output name"
                      />
                      <input
                        type="text"
                        className="output-description"
                        value={output.description}
                        onChange={(e) => handleOutputChange(step.id, index, 'description', e.target.value)}
                        placeholder="Output description"
                      />
                      {index > 0 ? (
                        <button
                          className="remove-output-btn"
                          onClick={() => removeOutput(step.id, index)}
                          title="Remove output"
                        >
                          ×
                        </button>
                      ) : (
                        <div className="remove-output-placeholder"></div>
                      )}
                    </div>
                  ))}
                  <button
                    className="add-output-btn"
                    onClick={() => addOutput(step.id)}
                    title="Add output"
                  >
                    +
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="config-field">
                <label>Processing Type</label>
                <select
                  value={step.processingType || 'number_operations'}
                  onChange={(e) => handleStepConfigChange(step.id, 'processingType', e.target.value)}
                >
                  <option value="number_operations">Number Operations</option>
                  <option value="string_operations">String Operations</option>
                  <option value="list_operations">List Operations</option>
                  <option value="python">Python Code</option>
                </select>
              </div>
              {renderProcessingConfig(step)}
              <div className="config-field">
                <label>Outputs</label>
                <div className="outputs-section">
                  {(step.outputs || [{ name: '', description: '' }]).map((output, index) => (
                    <div key={index} className="output-row">
                      <input
                        type="text"
                        className="output-name"
                        value={output.name}
                        onChange={(e) => handleOutputChange(step.id, index, 'name', e.target.value)}
                        placeholder="Output name"
                      />
                      <input
                        type="text"
                        className="output-description"
                        value={output.description}
                        onChange={(e) => handleOutputChange(step.id, index, 'description', e.target.value)}
                        placeholder="Output description"
                      />
                      {index > 0 ? (
                        <button
                          className="remove-output-btn"
                          onClick={() => removeOutput(step.id, index)}
                          title="Remove output"
                        >
                          ×
                        </button>
                      ) : (
                        <div className="remove-output-placeholder"></div>
                      )}
                    </div>
                  ))}
                  <button
                    className="add-output-btn"
                    onClick={() => addOutput(step.id)}
                    title="Add output"
                  >
                    +
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const handleChatSubmit = async () => {
    if (!userInput.trim() || !currentStepId) return;
    
    const newMessages = [...chatMessages, {role: 'user' as const, content: userInput}];
    setChatMessages(newMessages);
    setUserInput('');
    setIsGenerating(true);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const currentStepIndex = steps.findIndex(s => s.id === currentStepId);
      const previousSteps = steps.slice(0, currentStepIndex);
      const nextSteps = steps.slice(currentStepIndex + 1);
      
      const context = {
        inputSchema: convertInputFieldsToSchema(),
        outputMapping: convertOutputFieldsToMapping(),
        previousSteps,
        nextSteps,
        currentStep: steps[currentStepIndex],
        conversation: newMessages
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai-assistant/step-config`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...context, project_id: projectId, isPipelineMode: false })
      });
      
      const result = await response.json();
      
      if (result.stepConfig) {
        setSteps(steps.map(step => 
          step.id === currentStepId ? {
            ...step,
            ...result.stepConfig
          } : step
        ));
        setChatMessages([...newMessages, {role: 'assistant', content: 'Great! I\'ve generated the step configuration for you. You can review and modify it as needed.'}]);
      } else {
        setChatMessages([...newMessages, {role: 'assistant', content: result.message || 'I need more information to help you configure this step.'}]);
      }
    } catch (error) {
      setChatMessages([...newMessages, {role: 'assistant', content: 'Sorry, I encountered an error. Please try again.'}]);
    }
    
    setIsGenerating(false);
  };

  const handlePipelineChatSubmit = async () => {
    if (!pipelineUserInput.trim()) return;
    
    const newMessages = [...pipelineChatMessages, {role: 'user' as const, content: pipelineUserInput}];
    setPipelineChatMessages(newMessages);
    setPipelineUserInput('');
    setIsPipelineGenerating(true);
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const context = {
        inputSchema: convertInputFieldsToSchema(),
        outputMapping: convertOutputFieldsToMapping(),
        previousSteps: steps,
        nextSteps: [],
        currentStep: {},
        conversation: newMessages,
        isPipelineMode: true
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai-assistant/step-config`, {
        method: 'POST',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...context, project_id: projectId })
      });
      
      const result = await response.json();
      
      if (result.pipelineConfig) {
        const config = result.pipelineConfig;
        if (config.inputSchema?.properties) {
          const fields = Object.entries(config.inputSchema.properties).map(([name, prop]: [string, any]) => ({
            name,
            type: prop.type || 'string',
            description: prop.description || '',
            required: config.inputSchema.required?.includes(name) || false,
            defaultValue: prop.default || ''
          }));
          setInputFields(fields);
        }
        if (config.outputMapping) {
          const fields = Object.entries(config.outputMapping).map(([name, value]: [string, any]) => ({
            name,
            value: value || ''
          }));
          setOutputFields(fields);
        }
        if (config.steps) {
          const newSteps = config.steps.map((step: any, index: number) => ({
            ...step,
            id: index + 1,
            type: step.step_type === 'processing' ? 'processing' : 'llm'
          }));
          setSteps(newSteps);
          setNextStepId(newSteps.length + 1);
        }
        setPipelineChatMessages([...newMessages, {role: 'assistant', content: 'Perfect! I\'ve created your complete pipeline. You can review and modify any part as needed.'}]);
      } else {
        setPipelineChatMessages([...newMessages, {role: 'assistant', content: result.message || 'I need more information to help you build this pipeline.'}]);
      }
    } catch (error) {
      setPipelineChatMessages([...newMessages, {role: 'assistant', content: 'Sorry, I encountered an error. Please try again.'}]);
    }
    
    setIsPipelineGenerating(false);
  };

  return (
    <div className="module-content" style={{ position: 'relative' }}>
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        { label: 'Build' }
      ]} />
      <div className="build-header">
        <h2>Build LLM Application</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <span 
              style={{ 
                fontSize: '1.5rem', 
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                transition: 'background-color 0.2s'
              }}
              onClick={() => {
                setShowPipelineChat(true);
                setPipelineChatMessages([{role: 'assistant', content: 'Hi! I can help you build your entire pipeline. Describe what you want to achieve and I\'ll create the complete workflow for you.'}]);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              title="Pipeline AI Assistant"
            >
              🤖
            </span>
          </div>
          <div className="version-selector">
            <label>Version:</label>
            <select 
              value={selectedVersion} 
              onChange={(e) => handleVersionChange(e.target.value)}
              className="version-dropdown"
            >
              {versions.map((version) => (
                <option key={version.version} value={version.version}>
                  {version.display}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="build-pipeline">
        <div className="pipeline-section">
          <div className="section-card">
            <h3>📥 Inputs</h3>
            <p>Define your input fields</p>
            <div className="input-fields">
              {inputFields.map((field, index) => (
                <div key={index} className="input-field-row" style={{ padding: '20px', borderBottom: index < inputFields.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Field name:</label>
                      <input
                        type="text"
                        placeholder="Enter field name"
                        value={field.name}
                        onChange={(e) => {
                          const newFields = [...inputFields];
                          newFields[index].name = e.target.value;
                          setInputFields(newFields);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#3b82f6'}
                        onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Data Type:</label>
                      <select
                        value={field.type}
                        onChange={(e) => {
                          const newFields = [...inputFields];
                          newFields[index].type = e.target.value;
                          setInputFields(newFields);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          backgroundColor: 'white',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="boolean">Boolean</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151', paddingTop: '8px' }}>Description:</label>
                      <textarea
                        placeholder="Enter field description"
                        value={field.description}
                        onChange={(e) => {
                          const newFields = [...inputFields];
                          newFields[index].description = e.target.value;
                          setInputFields(newFields);
                        }}
                        style={{
                          flex: 1,
                          height: '60px',
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          resize: 'vertical',
                          outline: 'none',
                          fontFamily: 'inherit'
                        }}
                        onFocus={(e) => (e.target as HTMLTextAreaElement).style.borderColor = '#3b82f6'}
                        onBlur={(e) => (e.target as HTMLTextAreaElement).style.borderColor = '#d1d5db'}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Required:</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => {
                            const newFields = [...inputFields];
                            newFields[index].required = e.target.checked;
                            setInputFields(newFields);
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                        />
                        Yes
                      </label>
                    </div>
                    {!field.required && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Default:</label>
                        <input
                          type="text"
                          placeholder="Enter default value"
                          value={field.defaultValue}
                          data-field-type="defaultValue"
                          data-field-index={index}
                          onChange={(e) => handleInputChange(e, (value) => {
                            const newFields = [...inputFields];
                            newFields[index].defaultValue = value;
                            setInputFields(newFields);
                          })}
                          onKeyDown={handleKeyDown}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#3b82f6'}
                          onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                        />
                      </div>
                    )}
                    {inputFields.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            const newFields = inputFields.filter((_, i) => i !== index);
                            setInputFields(newFields);
                          }}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#dc2626'}
                          onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#ef4444'}
                        >
                          Remove Field
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  setInputFields([...inputFields, { name: '', type: 'string', description: '', required: true, defaultValue: '' }]);
                }}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  marginTop: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#059669'}
                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#10b981'}
              >
                Add Field
              </button>
            </div>
          </div>
        </div>
        
        {renderAddStepButton(0)}
        
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {renderStep(step)}
            {renderAddStepButton(index + 1)}
          </React.Fragment>
        ))}
        
        <div className="pipeline-section">
          <div className="section-card">
            <h3>📤 Outputs</h3>
            <p>Define output keys and their source paths</p>
            <div className="output-fields">
              {outputFields.map((field, index) => (
                <div key={index} className="output-field-row" style={{ padding: '20px', borderBottom: index < outputFields.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Field name:</label>
                      <input
                        type="text"
                        placeholder="Enter field name"
                        value={field.name}
                        onChange={(e) => {
                          const newFields = [...outputFields];
                          newFields[index].name = e.target.value;
                          setOutputFields(newFields);
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#3b82f6'}
                        onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ minWidth: '80px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Value:</label>
                      <input
                        type="text"
                        placeholder="e.g., $.input.message or $.step1.output.result"
                        value={field.value}
                        data-field-type="outputValue"
                        data-field-index={index}
                        onChange={(e) => handleInputChange(e, (value) => {
                          const newFields = [...outputFields];
                          newFields[index].value = value;
                          setOutputFields(newFields);
                        })}
                        onKeyDown={handleKeyDown}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none',
                          fontFamily: 'Monaco, monospace'
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).style.borderColor = '#3b82f6'}
                        onBlur={(e) => (e.target as HTMLInputElement).style.borderColor = '#d1d5db'}
                      />
                    </div>
                    {outputFields.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            const newFields = outputFields.filter((_, i) => i !== index);
                            setOutputFields(newFields);
                          }}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#dc2626'}
                          onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#ef4444'}
                        >
                          Remove Field
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  setOutputFields([...outputFields, { name: '', value: '' }]);
                }}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  marginTop: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#059669'}
                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#10b981'}
              >
                Add Field
              </button>
            </div>
          </div>
        </div>
        
        <div className="run-section">
          <button className="save-btn" onClick={handleSave}>
            Save
          </button>
          <button className="run-btn" onClick={handleRun}>
            Run
          </button>
          <button className="publish-btn" onClick={handlePublish}>
            Publish
          </button>
        </div>
      </div>
      
      {showChat && (
        <div style={{
          position: 'fixed',
          right: '20px',
          top: '20px',
          bottom: '20px',
          width: '400px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px 8px 0 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🤖</span>
              <span style={{ fontWeight: 'bold' }}>Step Assistant</span>
            </div>
            <button 
              onClick={() => setShowChat(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            flex: 1,
            padding: '1rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {chatMessages.map((msg, idx) => (
              <div key={idx} style={{
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '80%'
              }}>
                <div style={{
                  padding: '0.75rem',
                  borderRadius: '12px',
                  backgroundColor: msg.role === 'user' ? '#007bff' : '#f1f3f4',
                  color: msg.role === 'user' ? 'white' : 'black',
                  fontSize: '0.9rem',
                  lineHeight: '1.4'
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isGenerating && (
              <div style={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
                <div style={{
                  padding: '0.75rem',
                  borderRadius: '12px',
                  backgroundColor: '#f1f3f4',
                  fontSize: '0.9rem'
                }}>
                  Thinking...
                </div>
              </div>
            )}
          </div>
          
          <div style={{
            padding: '1rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '0.5rem'
          }}>
            <input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleChatSubmit()}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #ccc',
                borderRadius: '20px',
                outline: 'none',
                fontSize: '0.9rem'
              }}
            />
            <button
              onClick={handleChatSubmit}
              disabled={!userInput.trim() || isGenerating}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                cursor: userInput.trim() && !isGenerating ? 'pointer' : 'not-allowed',
                opacity: userInput.trim() && !isGenerating ? 1 : 0.5
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
      
      {showPipelineChat && (
        <div style={{
          position: 'fixed',
          right: '20px',
          top: '20px',
          bottom: '20px',
          width: '400px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#e8f5e8',
            borderRadius: '8px 8px 0 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🤖</span>
              <span style={{ fontWeight: 'bold' }}>Pipeline Assistant</span>
            </div>
            <button 
              onClick={() => setShowPipelineChat(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{
            flex: 1,
            padding: '1rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {pipelineChatMessages.map((msg, idx) => (
              <div key={idx} style={{
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '80%'
              }}>
                <div style={{
                  padding: '0.75rem',
                  borderRadius: '12px',
                  backgroundColor: msg.role === 'user' ? '#28a745' : '#f1f3f4',
                  color: msg.role === 'user' ? 'white' : 'black',
                  fontSize: '0.9rem',
                  lineHeight: '1.4'
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isPipelineGenerating && (
              <div style={{ alignSelf: 'flex-start', maxWidth: '80%' }}>
                <div style={{
                  padding: '0.75rem',
                  borderRadius: '12px',
                  backgroundColor: '#f1f3f4',
                  fontSize: '0.9rem'
                }}>
                  Building your pipeline...
                </div>
              </div>
            )}
          </div>
          
          <div style={{
            padding: '1rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '0.5rem'
          }}>
            <input
              type="text"
              value={pipelineUserInput}
              onChange={(e) => setPipelineUserInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePipelineChatSubmit()}
              placeholder="Describe your pipeline..."
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #ccc',
                borderRadius: '20px',
                outline: 'none',
                fontSize: '0.9rem'
              }}
            />
            <button
              onClick={handlePipelineChatSubmit}
              disabled={!pipelineUserInput.trim() || isPipelineGenerating}
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                cursor: pipelineUserInput.trim() && !isPipelineGenerating ? 'pointer' : 'not-allowed',
                opacity: pipelineUserInput.trim() && !isPipelineGenerating ? 1 : 0.5
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
      
      {autocomplete.show && (
        <div 
          className="autocomplete-dropdown"
          style={{
            top: autocomplete.position.top,
            left: autocomplete.position.left
          }}
        >
          {autocomplete.suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              className={`autocomplete-item ${index === autocomplete.selectedIndex ? 'selected' : ''}`}
              onClick={() => selectSuggestion(suggestion)}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      
      {/* Mini Pipeline Overview */}
      <div style={{
        position: 'fixed',
        right: '20px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '120px',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 100,
        fontSize: '10px'
      }}>
        <div style={{ marginBottom: '8px', textAlign: 'center', fontWeight: 'bold', color: '#666' }}>Pipeline</div>
        
        {/* Input */}
        <div 
          onClick={() => {
            const inputSection = document.querySelector('.pipeline-section h3')?.closest('.pipeline-section') as HTMLElement;
            if (inputSection) inputSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          style={{
            height: currentVisibleStep === 'inputs' ? '24px' : '20px',
            backgroundColor: '#e3f2fd',
            border: `${currentVisibleStep === 'inputs' ? '2px' : '1px'} solid #2196f3`,
            borderRadius: '4px',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#1976d2',
            transform: currentVisibleStep === 'inputs' ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}>📥</div>
        
        {/* Steps */}
        {steps.map((step, index) => {
          const isCurrentStep = currentVisibleStep === step.name;
          return (
            <div 
              key={step.id}
              title={step.name || `step${step.id}`}
              onClick={() => {
                const stepElements = document.querySelectorAll('.step-name-input');
                const targetStep = Array.from(stepElements).find(el => (el as HTMLInputElement).value === step.name);
                if (targetStep) {
                  const stepSection = targetStep.closest('.pipeline-section') as HTMLElement;
                  if (stepSection) stepSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              style={{
                height: isCurrentStep ? '24px' : '20px',
                backgroundColor: step.type === 'llm' ? '#fff3e0' : '#f3e5f5',
                border: `${isCurrentStep ? '2px' : '1px'} solid ${step.type === 'llm' ? '#ff9800' : '#9c27b0'}`,
                borderRadius: '4px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: step.type === 'llm' ? '#f57c00' : '#7b1fa2',
                transform: isCurrentStep ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}>
              {step.type === 'llm' ? '🔧' : '⚙️'}
            </div>
          );
        })}
        
        {/* Output */}
        <div 
          onClick={() => {
            const outputSection = Array.from(document.querySelectorAll('h3')).find(h3 => h3.textContent?.includes('Outputs'))?.closest('.pipeline-section') as HTMLElement;
            if (outputSection) outputSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          style={{
            height: currentVisibleStep === 'outputs' ? '24px' : '20px',
            backgroundColor: '#e8f5e8',
            border: `${currentVisibleStep === 'outputs' ? '2px' : '1px'} solid #4caf50`,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#388e3c',
            transform: currentVisibleStep === 'outputs' ? 'scale(1.1)' : 'scale(1)',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
          }}>📤</div>
        

      </div>
      
      <style>{`
        .flash-border {
          animation: flashBorder 3s ease-in-out;
        }
        
        @keyframes flashBorder {
          0%, 100% { border-color: transparent; }
          10%, 30%, 50%, 70%, 90% { border-color: #007bff; box-shadow: 0 0 10px rgba(0, 123, 255, 0.3); }
          20%, 40%, 60%, 80% { border-color: transparent; box-shadow: none; }
        }
      `}</style>
    </div>
  );
};

export default Build;